/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import orderInService from 'service/orderIn';
import { Coming } from 'data/coming';
import legalEntitySettingsService from 'service/legalEntitySettings';
import { pageToOffset } from 'utils/pageToOffset';
import Button from 'components/base/Button';
import ExcelIcon from 'pages/reports/purchase/filter/ExcelIcon';

interface ComingTableProps {
  dateFrom?: any;
  dateTo?: any;
  optionSenderId?: any;
  optionStatusId?: any;
  optionDelivererId?: any;
}

const ComingTable = ({
  dateFrom,
  dateTo,
  optionSenderId,
  optionStatusId,
  optionDelivererId
}: ComingTableProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const url = process.env.REACT_APP_CDN_URL;

  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);

  const navigateTo = (row: any) => {
    navigate(
      `/applications/coming/applications-coming-result/${row?.original?.gupid}`,
      {
        state: {
          orders: row?.original?.orders,
          sender_pid: row?.original?.sender_pid,
          recipient_pid: row?.original?.recipient_pid
        }
      }
    );
  };

  const { data: dataSettings } = useQuery(['GET_LEGAL_ENTITY_SETTINGS'], () => {
    const res = legalEntitySettingsService
      .getList({
        offset: 0,
        limit: 100
      })
      .then((res: any) => res?.legal_entity_settings);
    return res;
  });

  /* prettier-ignore */
  const adjustedDateTo =
    !dateFrom && !dateTo
      ? undefined
      : dateTo === dateFrom
        ? dateFrom + 86400000
        : dateTo
          ? dateTo + 86400000
          : undefined;

  const { data, isLoading, isFetching } = useQuery(
    [
      'GET_ORDER_IN_LIST',
      optionSenderId !== 0,
      optionStatusId !== 0,
      dateFrom,
      adjustedDateTo,
      currentPage,
      optionDelivererId
    ],
    async () => {
      const res = await orderInService
        .getList({
          offset: pageToOffset(currentPage, 10),
          limit: 10,
          state: optionStatusId ? optionStatusId : undefined,
          recipient_pid: optionSenderId ? optionSenderId : undefined,
          date_from: dateFrom,
          date_to: adjustedDateTo,
          deliver_employee_pid: optionDelivererId
            ? optionDelivererId
            : undefined
        })
        .then((res: any) => res);
      return res;
    },
    {
      enabled:
        !!currentPage || !!dateFrom || !!adjustedDateTo || !!optionDelivererId
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  /* prettier-ignore */
  const formatDateToTimeZone = (date: number, timeZone: string) => {
    try {
      // Format the date according to the specified time zone
      return new Intl.DateTimeFormat('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: timeZone
      }).format(new Date(date));
    } catch (error) {
      console.error('Error formatting date with time zone:', timeZone, error);
      return ''; // Return empty string if the time zone is invalid
    }
  };

  const comingData: Coming[] = useMemo(() => {
    let timeZone = dataSettings?.[0]?.timeZone?.name?.ru ?? 'UTC';

    timeZone = timeZone.replace(/\s*\(.*?\)/, '').trim();

    const resData =
      data?.orders?.map((items: any) => {
        return {
          name: items?.name,
          orders: items?.order_number,
          status: items?.state_info?.name?.[i18n?.language],
          sender: items?.legal_entity?.name,
          currency: items?.currency?.name?.[i18n?.language],
          recipient: items?.legal_entity?.name,
          sender_pid: items?.sender_pid,
          recipient_pid: items?.recipient_pid,
          /* prettier-ignore */
          date_request: formatDateToTimeZone(items?.order_date, timeZone),
          date_delivery: formatDateToTimeZone(items?.delivered_date, timeZone),
          date_completion: formatDateToTimeZone(
            items?.recipient_approved_date,
            timeZone
          ),
          can_update: items?.can_update || false,
          maturity_date: items?.maturity_date ?? null,
          // date_auto_cancel: items?.date_auto_cancel
          //   ? items?.date_auto_cancel
          //   : milliseconds,
          delivery:
            (items?.deliverer?.first_name || '') +
            ' ' +
            (items?.deliverer?.last_name || ''),
          gupid: items?.pid
        };
      }) ?? [];

    return resData;
  }, [data, dataSettings, t, i18n?.language]);

  const handleExcelById = (pid: number) => {
    const customFilename: string = 'Закуп по накладной.xlsx';

    // Fetch report and trigger download
    orderInService
      ?.getOrderInListExcel({ order_pid: pid, order_type: 'IN' })
      .then((res: any) => {
        if (res?.filename !== '') {
          const resUrl = `${url}${res?.filename}`;
          const link = document.createElement('a');
          link.href = resUrl;
          link.download = customFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
  };

  const comingTableColumns: ColumnDef<Coming>[] = [
    {
      id: 'action1',
      cell: rowData => {
        const { can_update } = rowData.row.original;
        return (
          <div>
            {!can_update && (
              <Button
                variant="hover"
                onClick={(e: any) => {
                  handleExcelById(rowData?.row?.original?.gupid);
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ExcelIcon />
              </Button>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '5%', textAlign: 'center' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'order_number',
      header: '№',
      cell: rowData => {
        const orderNumber = rowData.row.index + 1 + (currentPage - 1) * 10;
        return <div>{orderNumber}</div>;
      },
      meta: {
        headerProps: { style: { width: '5%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'orders',
      header: t('application_number'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'recipient',
      header: t('recipient'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'delivery',
      header: t('delivery_man'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_request',
      header: t('date_request'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_delivery',
      header: t('date_delivery'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'date_completion',
      header: t('date_completion'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: comingData,
    columns: comingTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  // useEffect(() => {
  //   refetch();
  // }, [dateFrom, dateTo, optionSenderId, optionStatusId]);

  return (
    <AdvanceTableProvider {...table}>
      <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
        <AdvanceTable
          tableProps={{ className: 'phoenix-table fs-9' }}
          navigateTo={navigateTo}
          isLoading={loading}
        />
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </AdvanceTableProvider>
  );
};

export default ComingTable;
